"use client";

import type { LinkProps } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { X, Menu } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useState, useContext, createContext } from 'react';

interface Links {
  label: string;
  href: string;
  icon: React.JSX.Element | React.ReactNode;
}

interface SidebarContextProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  animate: boolean;
  isDocked?: boolean;
  setIsDocked?: React.Dispatch<React.SetStateAction<boolean>>;
}

const SidebarContext = createContext<SidebarContextProps | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export const SidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true,
  isDocked: isDockedProp,
  setIsDocked: setIsDockedProp,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
  isDocked?: boolean;
  setIsDocked?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [openState, setOpenState] = useState(false);
  const [isDockedState, setIsDockedState] = useState(false);

  const open = openProp !== undefined ? openProp : openState;
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState;
  
  const isDocked = isDockedProp !== undefined ? isDockedProp : isDockedState;
  const setIsDocked = setIsDockedProp !== undefined ? setIsDockedProp : setIsDockedState;

  return (
    <SidebarContext.Provider value={{ open, setOpen, animate, isDocked, setIsDocked }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const Sidebar = ({
  children,
  open,
  setOpen,
  animate,
  isDocked,
  setIsDocked,
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
  isDocked?: boolean;
  setIsDocked?: React.Dispatch<React.SetStateAction<boolean>>;
}) => (
  <SidebarProvider open={open} setOpen={setOpen} animate={animate} isDocked={isDocked} setIsDocked={setIsDocked}>
    {children}
  </SidebarProvider>
);

export const SidebarBody = (props: React.ComponentProps<'div'>) => (
  <>
    <DesktopSidebar {...props} />
    <MobileSidebar {...props} />
  </>
);

export const DesktopSidebar = ({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) => {
  const { open, setOpen, animate, isDocked } = useSidebar();
  return (
    <div
      className={cn(
        'hidden h-full flex-shrink-0 border-r border-border/40 bg-white py-2 text-foreground transition-[width] duration-200 ease-in-out dark:bg-zinc-950 md:flex md:flex-col',
        className,
      )}
      style={{ width: animate ? (open || isDocked ? '300px' : '60px') : '300px' }}
      onMouseEnter={() => {
        if (!isDocked) {
          setOpen(true);
        }
      }}
      onMouseLeave={() => {
        if (!isDocked) {
          setOpen(false);
        }
      }}
      {...props}
    >
      {children}
    </div>
  );
};

export const MobileSidebar = ({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) => {
  const { open, setOpen } = useSidebar();
  return (
    <div
      className={cn(
        'flex h-10 w-full flex-row items-center justify-between border-b border-border bg-background px-4 py-4 text-foreground md:hidden',
      )}
      {...props}
    >
      <div className="z-20 flex w-full justify-end">
        <Menu className="cursor-pointer text-foreground" onClick={() => setOpen(!open)} />
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: '-100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: 'easeInOut',
            }}
            className={cn(
              'fixed inset-0 z-[100] flex h-full w-full flex-col justify-between border-r border-border bg-background p-10 text-foreground',
              className,
            )}
          >
            <div
              className="absolute right-10 top-10 z-50 cursor-pointer text-foreground"
              onClick={() => setOpen(!open)}
            >
              <X />
            </div>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const SidebarLink = ({
  link,
  className,
  ...props
}: {
  link: Links;
  className?: string;
  props?: LinkProps;
}) => {
  const { open, animate } = useSidebar();
  return (
    <Link
      to={link.href}
      className={cn('group/sidebar flex items-center justify-start gap-2 py-2', className)}
      {...props}
    >
      {link.icon}
      <span
        className={cn(
          'whitespace-pre text-sm text-neutral-700 !m-0 !p-0 transition-opacity duration-200 group-hover/sidebar:translate-x-1 dark:text-neutral-200',
          animate
            ? open
              ? 'inline-block opacity-100'
              : 'hidden opacity-0'
            : 'inline-block opacity-100',
        )}
      >
        {link.label}
      </span>
    </Link>
  );
};
